import { useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { useDashboardLayoutStore } from '../stores/dashboardLayoutStore'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import {
    Chart as ChartJS,
    CategoryScale, LinearScale,
    BarElement, BarController,
    LineElement, LineController, PointElement,
    ArcElement,
    Tooltip, Legend, Filler
} from 'chart.js'
import { Navigate, useSearchParams } from 'react-router-dom'
import { api, API_BASE } from '../lib/api'
import { fmt, localDateStr, KPI_ACCENTS, KPI_NO_COMPARISONS } from '../lib/dashboardHelpers'
import type { KpiComparison, DashboardData, StoreConfig, DebugQuery } from '../types/dashboard'
import {
    KpiCard, HourlyChart, PaymentDonut, ChannelMixBar,
    TopSellersPanel, SoldOutPanel, WidgetGrid, SqlDebugModal, WeeklySalesChart, MobileFooter, SalesByStoreChart, DailyPerformanceCountdown
} from '../components/dashboard'
import WidgetGallery from '../components/WidgetGallery'
import LayoutPresetsMenu from '../components/dashboard/LayoutPresetsMenu'
import '../styles/dashboard.css'
import '../styles/widget-gallery.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, LineElement, LineController, PointElement, ArcElement, Tooltip, Legend, Filler)

// ── Main Component ───────────────────────────────────────────────────

export default function DashboardPage() {
    const user = useAuthStore(s => s.user)
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()

    // Layout store
    const {
        layouts, activeWidgets, widgetSettings, isEditing, layoutVersion,
        primaryComparison, setPrimaryComparison, currentLayoutSource,
        compareFull, setCompareFull,
        availableLayouts, currentLayoutId, currentLayoutName, currentLayoutIsDefault,
        setEditing, removeWidget, setLayouts, snapshotLayouts,
        revertToInitial, updateWidgetSettings, resetLayout, resetToSystemDefault,
        loadFromServer, loadLayout, saveToAccount, saveAsSystemDefault,
        hasUnsavedChanges, applyImportedLayout,
    } = useDashboardLayoutStore()

    // Store config (which stores this user can see)
    const { data: storeConfig, isLoading: configLoading } = useQuery<StoreConfig>({
        queryKey: ['dashboard-stores'],
        queryFn: () => api('/api/dashboard/stores', token),
        staleTime: 60_000
    })

    const [searchParams, setSearchParams] = useSearchParams()

    const [storeId, setStoreId] = useState<string>(searchParams.get('storeId') || 'group')
    const [channel, setChannel] = useState<string>(searchParams.get('channel') || 'all')
    const [includeReturns, setIncludeReturns] = useState(searchParams.get('includeReturns') !== 'false')
    const [includeMultibuys, setIncludeMultibuys] = useState(searchParams.get('includeMultibuys') !== 'false')
    const [showDebug, setShowDebug] = useState(false)
    const [showGallery, setShowGallery] = useState(false)
    const [showSaveDialog, setShowSaveDialog] = useState(false)
    const [saveLayoutName, setSaveLayoutName] = useState('')
    const [saveLayoutIsNew, setSaveLayoutIsNew] = useState(false)
    const [saveLayoutIsDefault, setSaveLayoutIsDefault] = useState(false)
    const [overwriteSystemDefault, setOverwriteSystemDefault] = useState(false)
    const [overwriteUserCustomizations, setOverwriteUserCustomizations] = useState(false)
    const [showResetConfirm, setShowResetConfirm] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState<string | null>(null)
    const [actionMenuOpen, setActionMenuOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState(() => searchParams.get('date') || localDateStr())

    const isToday = selectedDate === localDateStr()
    const shiftDate = (days: number) => {
        const d = new Date(selectedDate + 'T12:00:00')
        d.setDate(d.getDate() + days)
        setSelectedDate(localDateStr(d))
    }
    const goToday = () => setSelectedDate(localDateStr())
    const autoRefresh = isToday ? 120_000 : false

    // Set defaults from user preferences once loaded
    useEffect(() => {
        if (storeConfig) {
            const initialParams = new URLSearchParams(window.location.search)
            if (!initialParams.has('storeId')) setStoreId(storeConfig.defaultView)
            if (!initialParams.has('channel')) setChannel(storeConfig.defaultChannel)
        }
    }, [storeConfig])

    // Load layout from server
    useEffect(() => {
        if (token && user) {
            const requestedLayoutId = new URLSearchParams(window.location.search).get('layout')
            if (requestedLayoutId) {
                loadLayout(token, user.id, 'daily', requestedLayoutId)
            } else {
                loadFromServer(token, user.id)
            }
        }
    }, [token, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (currentLayoutId) params.set('layout', currentLayoutId)
        if (storeId !== 'group') params.set('storeId', storeId)
        if (channel !== 'all') params.set('channel', channel)
        if (!includeReturns) params.set('includeReturns', 'false')
        if (!includeMultibuys) params.set('includeMultibuys', 'false')
        if (selectedDate !== localDateStr()) params.set('date', selectedDate)
        
        setSearchParams(params, { replace: true })
    }, [currentLayoutId, storeId, channel, includeReturns, includeMultibuys, selectedDate, setSearchParams])

    // Save preference on change
    const savePreference = useCallback((view: string, ch: string) => {
        fetch(API_BASE + '/api/user/preferences', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ dashboardDefaultView: view, dashboardDefaultChannel: ch })
        })
    }, [token])

    // Dashboard data
    const { data, isLoading, isFetching, error, dataUpdatedAt } = useQuery<DashboardData>({
        queryKey: ['dashboard-daily', storeId, channel, includeReturns, compareFull, includeMultibuys, selectedDate],
        queryFn: () => api(`/api/dashboard/daily?storeId=${storeId}&channel=${channel}&includeReturns=${includeReturns}&compareFull=${compareFull}&includeMultibuys=${includeMultibuys}&date=${selectedDate}`, token),
        refetchInterval: autoRefresh,
        enabled: !!storeConfig,
        placeholderData: keepPreviousData,
        retry: false
    })

    const hasSalesByStoreWidget = activeWidgets.includes('salesByStore')
    const { data: salesByStoreData, isFetching: salesByStoreFetching } = useQuery<any[]>({
        queryKey: ['dashboard-sales-by-store-daily', storeId, channel, includeReturns, includeMultibuys, selectedDate],
        queryFn: () => api(`/api/dashboard/sales-by-store?period=daily&storeId=${storeId}&channel=${channel}&includeReturns=${includeReturns}&includeMultibuys=${includeMultibuys}&date=${selectedDate}`, token),
        refetchInterval: autoRefresh,
        enabled: !!storeConfig && hasSalesByStoreWidget,
        placeholderData: keepPreviousData,
        retry: false
    })

    const refresh = () => qc.invalidateQueries({ queryKey: ['dashboard-daily'] })

    const hour = new Date().getHours()
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

    const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'HeadOffice'

    // ── Save handlers ────────────────────────────────────────────────

    const handleCancel = () => {
        revertToInitial()
    }

    const handleSave = () => {
        setSaveLayoutName(currentLayoutName || '')
        setSaveLayoutIsNew(!currentLayoutId)
        setSaveLayoutIsDefault(currentLayoutIsDefault)
        setOverwriteSystemDefault(false)
        setOverwriteUserCustomizations(false)
        setShowSaveDialog(true)
    }

    const handleSaveAccount = async () => {
        setShowSaveDialog(false)
        setEditing(false)
        const isNew = saveLayoutIsNew || !currentLayoutId
        if (token) await saveToAccount(token, saveLayoutName, saveLayoutIsDefault, isNew, storeConfig?.defaultView === 'monthly' ? 'monthly' : 'daily')
    }

    const handleSaveSystemDefault = async () => {
        setShowSaveDialog(false)
        setEditing(false)
        try {
            if (token) {
                console.log('[dashboard] handleSaveSystemDefault — saving...')
                await saveAsSystemDefault(token, 'daily', overwriteUserCustomizations)
                console.log('[dashboard] handleSaveSystemDefault — done OK')
            } else {
                console.warn('[dashboard] handleSaveSystemDefault — no token!')
            }
        } catch (e) {
            console.error('[dashboard] handleSaveSystemDefault FAILED:', e)
        }
    }

    const handleReset = async () => {
        try {
            if (token && user) {
                console.log('[dashboard] handleReset — calling resetToSystemDefault...')
                await resetToSystemDefault(token, user.id)
                console.log('[dashboard] handleReset — done OK')
            } else {
                console.warn('[dashboard] handleReset — no token/user, using local resetLayout')
                resetLayout()
            }
        } catch (e) {
            console.error('[dashboard] handleReset FAILED:', e)
        } finally {
            setShowResetConfirm(false)
        }
    }

    // ── Layout preset handlers ────────────────────────────────────────

    const handlePresetSelect = (layoutId: string) => {
        loadLayout(token!, user!.id, 'daily', layoutId)
    }

    const handleExportLayout = () => {
        return JSON.stringify({
            layouts, activeWidgets, widgetSettings, primaryComparison,
        }, null, 2)
    }

    const handleImportLayout = (json: string): string | null => {
        let parsed: any
        try {
            parsed = JSON.parse(json)
        } catch {
            return 'Invalid JSON syntax. Please check the format and try again.'
        }

        if (!parsed.activeWidgets || !Array.isArray(parsed.activeWidgets)) {
            return 'Invalid layout: missing "activeWidgets" array.'
        }
        if (!parsed.layouts || typeof parsed.layouts !== 'object') {
            return 'Invalid layout: missing "layouts" object.'
        }

        applyImportedLayout(parsed)
        return null
    }

    // ── Widget renderer ──────────────────────────────────────────────

    const renderWidget = (widgetId: string) => {
        if (!data?.success) return null
        const settings = widgetSettings[widgetId] ?? { size: 'full', showLastWeek: true, showYesterday: true, showLastYear: true }

        // KPI widgets
        const kpiQueries = isAdmin ? (data.debug?.filter(q => q.label.startsWith(`KPI — ${data.date}`)) || []) : []
        const invQueries = isAdmin ? (data.debug?.filter(q => q.label === 'Inventory Metrics') || []) : []

        const kpiMap: Record<string, { label: string; kpi: KpiComparison; format?: 'currency' | 'percent' | 'int' | 'decimal'; lowerIsBetter?: boolean; description: string; queries?: DebugQuery[] }> = {
            netSales: { label: includeReturns ? 'Net Sales' : 'Gross Sales', kpi: data.kpis.netSales, format: 'currency', description: "Total revenue calculated using the transaction subtotal (excluding taxes).\n\nNet Sales includes product returns if the 'Net (with returns)' toggle is enabled.", queries: kpiQueries.filter(q => q.label.includes('(Tx-level)')) },
            margin: {
                label: 'Margin', kpi: {
                    today: data.kpis.netSales.today > 0 ? data.kpis.margin.today / data.kpis.netSales.today : 0,
                    lastWeek: data.kpis.netSales.lastWeek > 0 ? data.kpis.margin.lastWeek / data.kpis.netSales.lastWeek : 0,
                    yesterday: data.kpis.netSales.yesterday > 0 ? data.kpis.margin.yesterday / data.kpis.netSales.yesterday : 0,
                    lastYear: data.kpis.netSales.lastYear > 0 ? data.kpis.margin.lastYear / data.kpis.netSales.lastYear : 0,
                }, format: 'percent', description: "Gross margin calculated as: ((Retail Price - Item Discount) - Cost) * Quantity.\n\nThis is aggregated from individual receipt items. The percentage is Margin / Net Sales.", queries: kpiQueries.filter(q => q.label.includes('(Item-level)'))
            },
            costOfSales: { label: 'Cost of Sales', kpi: data.kpis.costOfSales, format: 'currency', description: "Total cost of goods sold, calculated as Cost * Quantity from individual receipt items.", queries: kpiQueries.filter(q => q.label.includes('(Item-level)')) },
            transactions: { label: `Transactions${channel === 'instore' ? ' - In-Store' : channel === 'online' ? ' - Online' : ' - All Channels'}`, kpi: data.kpis.transactions, description: "Total number of completed receipts/transactions.", queries: kpiQueries.filter(q => q.label.includes('(Tx-level)')) },
            unitsSold: { label: 'Units Sold', kpi: data.kpis.unitsSold, description: "Total number of items sold.\n\nIf 'Net' mode is enabled, this includes returned items (which count as negative units).", queries: kpiQueries.filter(q => q.label.includes('(Item-level)')) },
            avgTransaction: { label: 'Avg Transaction', kpi: data.kpis.avgTransaction, format: 'currency', description: "Average revenue per transaction.\n\nCalculated as Net Sales / Transactions.", queries: kpiQueries.filter(q => q.label.includes('(Tx-level)')) },
            upt: { label: 'Units Per Txn', kpi: data.kpis.upt, format: 'decimal', description: "Units Per Transaction.\n\nCalculated as Units Sold / Transactions.", queries: kpiQueries },
            discountDepth: { label: 'Discount Depth', kpi: data.kpis.discountDepth, format: 'percent', lowerIsBetter: true, description: "The percentage of the total retail value that was discounted.\n\nCalculated as Total Item Discount / (Net Sales + Total Item Discount).", queries: kpiQueries.filter(q => q.label.includes('(Item-level)')) },
            sellThrough: { label: 'Sell-Through', kpi: data.kpis.sellThrough, format: 'percent', description: "The percentage of available inventory that was sold.\n\nCalculated as Units Sold / (Units Sold + Remaining Inventory).", queries: invQueries },
            inventoryValue: { label: 'Inv Value on Hand', kpi: data.kpis.inventoryValue, format: 'currency', description: "The estimated total cost value of all currently on-hand inventory.", queries: invQueries },
            gmroi: { label: 'Est. GMROI (Ann.)', kpi: data.kpis.gmroi, format: 'percent', description: "Gross Margin Return on Investment (annualized).\n\nAn estimate of the margin earned per dollar invested in inventory.", queries: invQueries },
        }

        if (kpiMap[widgetId]) {
            const { label, kpi, format, lowerIsBetter, description, queries } = kpiMap[widgetId]
            const fmtStyle = format ?? 'int'
            const value = fmt(kpi.today, fmtStyle)

            return (
                <KpiCard
                    label={label} value={value} kpi={kpi}
                    format={format} accent={KPI_ACCENTS[widgetId]}
                    lowerIsBetter={lowerIsBetter}
                    hideComparisons={KPI_NO_COMPARISONS.has(widgetId)}
                    showLastWeek={settings.showLastWeek}
                    showYesterday={settings.showYesterday}
                    showLastMonth={settings.showLastMonth}
                    showLastYear={settings.showLastYear}
                    showPctChange={settings.showPctChange}
                    showNetChange={settings.showNetChange}
                    primaryComparison={primaryComparison}
                    description={description}
                    adminQueries={queries}
                />
            )
        }

        switch (widgetId) {
            case 'channelMix':
                return data.channelMix ? <ChannelMixBar mix={data.channelMix} /> : null
            case 'hourlyChart':
                return <HourlyChart hourly={data.hourly ?? []} settings={settings} channel={channel} />
            case 'weeklySales':
                return <WeeklySalesChart weekly={data.weeklySales ?? []} selectedDate={selectedDate} settings={settings} onDateSelect={setSelectedDate} />
            case 'paymentMix':
                return <PaymentDonut mix={data.paymentMix} />
            case 'topSellers':
                const dayTitle = isToday ? 'Top Sellers Today' : `Top Sellers ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                return <TopSellersPanel sellers={data.topSellers} channel={channel} title={dayTitle} />
            case 'soldOut':
                return <SoldOutPanel items={data.soldOutItems} />
            case 'salesByStore':
                return <SalesByStoreChart data={salesByStoreData || []} settings={settings} selectedStoreId={storeId} />
            case 'dailyPerformance':
                const todaySales = data.kpis.netSales?.today || 0
                const hWeekly = data.weeklySales || []
                const lyAvg = hWeekly.length > 0 ? hWeekly.reduce((sum, day) => sum + (day.lastYearSales || 0), 0) / 7 : 0
                
                const targetMode = settings.dailyTargetMode || 'lastYearWeeklyAvg'
                const targetMultiplier = settings.dailyTargetMultiplier || 0
                const targetFixedAmount = settings.dailyTargetFixedAmount || 10000
                
                let target = 0
                if (targetMode === 'fixed') {
                    target = targetFixedAmount
                } else if (targetMode === 'lastYearWeeklyCumulative') {
                    const lyWeeklyTotal = hWeekly.reduce((sum, day) => sum + (day.lastYearSales || 0), 0)
                    const weeklyTarget = lyWeeklyTotal * (1 + targetMultiplier / 100)
                    
                    const selDateObj = new Date(selectedDate + 'T12:00:00')
                    const currentDowInt = selDateObj.getDay()
                    const daysPassed = (currentDowInt + 6) % 7 // Mon=0, Sun=6
                    const remainingDays = 7 - daysPassed
                    
                    const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                    const pastDaysOfWeek = weekDays.slice(0, daysPassed)
                    
                    let salesSoFar = 0
                    for (const dayStr of pastDaysOfWeek) {
                        const bucket = hWeekly.find(d => d.dayOfWeek === dayStr)
                        if (bucket) salesSoFar += bucket.sales || 0
                    }
                    
                    const defaultAvgTarget = lyAvg * (1 + targetMultiplier / 100)
                    const dynamicTarget = (weeklyTarget - salesSoFar) / remainingDays
                    
                    target = Math.max(dynamicTarget, defaultAvgTarget)
                } else {
                    target = lyAvg * (1 + targetMultiplier / 100)
                }

                return <DailyPerformanceCountdown todaySales={todaySales} target={target} targetMode={targetMode} targetMultiplier={targetMultiplier} isPastDate={selectedDate < localDateStr()} />
            default:
                return <div style={{ padding: '1rem', color: 'var(--color-text-tertiary)' }}>Unknown widget: {widgetId}</div>
        }
    }

    if (configLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
                <div className="page-header"><h1>{greeting}, {user?.displayName.split(' ')[0]} 👋</h1></div>
                <div className="dashboard-loading"><div className="spinner" /><span>Loading configuration…</span></div>
            </div>
        )
    }

    if (user?.role === 'Unassigned') return <Navigate to="/my-stores" replace />
    if (storeConfig && storeConfig.stores.length === 0 && !storeConfig.canViewAll) return <Navigate to="/my-stores" replace />

    return (
        <>
            <div className="page-header">
                <h1>{greeting}, {user?.displayName.split(' ')[0]} 👋</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <select
                        className="header-store-select"
                        value={storeId}
                        onChange={e => { setStoreId(e.target.value); savePreference(e.target.value, channel) }}
                    >
                        {storeConfig?.stores.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                        {storeConfig?.hasGroup && <option value="group">My Store Group</option>}
                        {storeConfig?.canViewAll && <option value="all">All Stores</option>}
                    </select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }} className="desktop-date-selector">
                        <button className="toolbar-refresh-btn" onClick={() => shiftDate(-1)} title="Previous day">◀</button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={e => setSelectedDate(e.target.value)}
                            style={{
                                background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)', padding: '4px 6px',
                                color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                                fontFamily: 'inherit', cursor: 'pointer',
                                colorScheme: 'dark', maxWidth: '140px'
                            }}
                        />
                        <button className="toolbar-refresh-btn" onClick={() => shiftDate(1)} title="Next day">▶</button>
                        {!isToday && (
                            <button className="toolbar-refresh-btn" onClick={goToday}
                                style={{ fontSize: 'var(--text-xs)' }}>Today</button>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                            Compare to:
                        </span>
                        <select
                            value={primaryComparison}
                            onChange={e => {
                                const val = e.target.value as any
                                setPrimaryComparison(val)
                                // Sync chart comparison settings with Compare To selection
                                if (val !== 'none') {
                                    updateWidgetSettings('weeklySales', {
                                        showLastWeek: val === 'lastWeek',
                                        showLastMonth: val === 'lastMonth',
                                        showLastYear: val === 'lastYear',
                                    })
                                }
                            }}
                            className="header-store-select"
                        >
                            <option value="none">None</option>
                            <option value="yesterday">Day</option>
                            <option value="lastWeek">Week</option>
                            <option value="lastMonth">Month</option>
                            <option value="lastYear">Year</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────────────────────────── */}
            <div className="dashboard-toolbar">

                <div className="seg-control">
                    {(['all', 'instore', 'online'] as const).map(ch => (
                        <button key={ch} className={channel === ch ? 'active' : ''}
                            onClick={() => { setChannel(ch); savePreference(storeId, ch) }}>
                            {ch === 'all' ? 'ALL' : ch === 'instore' ? 'IN-STORE' : 'ONLINE'}
                        </button>
                    ))}
                </div>

                <div className="seg-control">
                    <button className={includeReturns ? 'active' : ''} onClick={() => setIncludeReturns(true)}>NET</button>
                    <button className={!includeReturns ? 'active' : ''} onClick={() => setIncludeReturns(false)}>GROSS</button>
                </div>



                <label className="toolbar-checkbox">
                    <input
                        type="checkbox"
                        checked={includeMultibuys}
                        onChange={(e) => setIncludeMultibuys(e.target.checked)}
                    />
                    Include Multibuys
                </label>

                <label className="toolbar-checkbox">
                    <input
                        type="checkbox"
                        checked={compareFull}
                        onChange={(e) => setCompareFull(e.target.checked)}
                    />
                    Compare Full
                </label>

                {!isEditing ? (
                    <div className="desktop-actions-menu-container" style={{ position: 'relative', marginLeft: 'auto' }}>
                        <button className="toolbar-refresh-btn" onClick={() => setActionMenuOpen(x => !x)} title="Dashboard Actions">
                            ⚙ Actions
                        </button>
                        {actionMenuOpen && (
                            <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                                <div className="dashboard-actions-menu">
                                    {dataUpdatedAt > 0 && (
                                        <div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>
                                            Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                                        </div>
                                    )}
                                    <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false); }} style={{ padding: '8px 12px' }}>
                                        <span className="nav-icon">↻</span>
                                        <span className="nav-label">Refresh</span>
                                    </button>
                                    <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px' }}>
                                        <span className="nav-icon">✏️</span>
                                        <span className="nav-label">Edit Layout</span>
                                    </button>
                                    {isAdmin && (
                                        <button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', color: 'var(--color-text-tertiary)' }}>
                                            <span className="nav-icon">🐛</span>
                                            <span className="nav-label">Debug SQL</span>
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginLeft: 'auto' }}>
                        <button className="edit-mode-btn" onClick={() => setShowGallery(true)}>
                            ➕ Add Widget
                        </button>
                        <LayoutPresetsMenu
                            availableLayouts={availableLayouts}
                            currentLayoutId={currentLayoutId}
                            currentLayoutSource={currentLayoutSource}
                            isEditing={isEditing}
                            hasUnsavedChanges={hasUnsavedChanges()}
                            dashboardType="daily"
                            onSelectLayout={handlePresetSelect}
                            onExport={handleExportLayout}
                            onImport={handleImportLayout}
                            onSaveFirst={handleSave}
                        />
                        <button className="edit-mode-btn" onClick={() => setShowResetConfirm(true)}
                            style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
                            ↺ Reset
                        </button>
                        <button className="edit-mode-btn cancel" onClick={handleCancel}>
                            ✕ Cancel
                        </button>
                        <button className="edit-mode-btn done" onClick={handleSave}>
                            💾 Save
                        </button>
                    </div>
                )}
            </div>

            {/* ── Debug Modal ───────────────────────────────────────── */}
            {showDebug && <SqlDebugModal queries={data?.debug ?? []} activeProfile={data?.activeProfile} connectionInfo={data?.connectionInfo} onClose={() => setShowDebug(false)} />}

            {/* ── Widget Gallery ────────────────────────────────────── */}
            {showGallery && <WidgetGallery onClose={() => setShowGallery(false)} dashboardType="daily" />}

            {/* ── Save Dialog ───────────────────────────────────────── */}
            {showSaveDialog && (
                <div className="admin-save-overlay" onClick={() => setShowSaveDialog(false)}>
                    <div className="admin-save-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Save Layout</h3>
                        
                        <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', cursor: currentLayoutId ? 'pointer' : 'not-allowed', color: currentLayoutId ? 'inherit' : 'var(--color-text-tertiary)' }}>
                                <input type="radio" checked={!saveLayoutIsNew} onChange={() => setSaveLayoutIsNew(false)} disabled={!currentLayoutId} style={{ marginRight: '8px' }} />
                                Overwrite Current Layout {currentLayoutName ? `(${currentLayoutName})` : ''}
                            </label>
                            <label style={{ display: 'block', fontSize: '14px', cursor: 'pointer' }}>
                                <input type="radio" checked={saveLayoutIsNew || !currentLayoutId} onChange={() => setSaveLayoutIsNew(true)} style={{ marginRight: '8px' }} />
                                Save as New Layout
                            </label>
                        </div>

                        {(saveLayoutIsNew || !currentLayoutId) && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>Layout Name</label>
                                <input 
                                    type="text" 
                                    value={saveLayoutName} 
                                    onChange={e => setSaveLayoutName(e.target.value)}
                                    placeholder="My Custom Layout"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '14px' }}
                                    autoFocus
                                />
                            </div>
                        )}

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label className="admin-save-checkbox">
                                <input type="checkbox" checked={saveLayoutIsDefault} onChange={e => setSaveLayoutIsDefault(e.target.checked)} />
                                Set as my User Default to load on login
                            </label>
                        </div>

                        <div className="admin-save-actions">
                            <button className="btn btn-primary" onClick={handleSaveAccount} disabled={(saveLayoutIsNew || !currentLayoutId) && !saveLayoutName.trim()}>
                                Save Layout
                            </button>
                        </div>

                        {isAdmin && (
                            <>
                                <div className="admin-save-divider" />
                                <h4 style={{ marginBottom: '0.5rem' }}>Admin Options</h4>
                                <label className="admin-save-checkbox">
                                    <input type="checkbox" checked={overwriteSystemDefault}
                                        onChange={e => {
                                            setOverwriteSystemDefault(e.target.checked)
                                            if (!e.target.checked) setOverwriteUserCustomizations(false)
                                        }} />
                                    Overwrite System Default
                                </label>
                                <label className="admin-save-checkbox" style={{ marginTop: '8px', opacity: overwriteSystemDefault ? 1 : 0.5 }}>
                                    <input type="checkbox" checked={overwriteUserCustomizations}
                                        onChange={e => {
                                            if (overwriteSystemDefault) setOverwriteUserCustomizations(e.target.checked)
                                        }} disabled={!overwriteSystemDefault} />
                                    Clear User Defaults (Force this layout)
                                </label>
                                <div className="admin-save-actions" style={{ marginTop: '1rem' }}>
                                    <button className="btn btn-primary" onClick={handleSaveSystemDefault}
                                        disabled={!overwriteSystemDefault}
                                        style={!overwriteSystemDefault ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>
                                        Save for Everyone
                                    </button>
                                </div>
                            </>
                        )}
                        <div className="admin-save-actions" style={{ marginTop: '0.5rem' }}>
                            <button className="btn btn-secondary" onClick={() => setShowSaveDialog(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Reset Confirmation ───────────────────────────────── */}
            {showResetConfirm && (
                <div className="reset-confirm-overlay" onClick={() => setShowResetConfirm(false)}>
                    <div className="reset-confirm-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Reset Dashboard Layout?</h3>
                        <p>This will remove all customizations and restore the default widget arrangement. This cannot be undone.</p>
                        <div className="reset-confirm-actions">
                            <button className="btn btn-secondary" onClick={() => setShowResetConfirm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleReset}
                                style={{ background: 'var(--color-error)', borderColor: 'var(--color-error)' }}>
                                Reset Layout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Content ────────────────────────────────────────────── */}
            {isLoading && (
                <div className="dashboard-loading"><div className="spinner" /><span>Loading dashboard…</span></div>
            )}
            {error && <div className="dashboard-error">Failed to load dashboard data. Please try again.</div>}
            {data && !data.success && <div className="dashboard-error">{data.errorMessage}</div>}

            {data?.success && (
                <WidgetGrid
                    layouts={layouts}
                    activeWidgets={activeWidgets}
                    widgetSettings={widgetSettings}
                    isEditing={isEditing}
                    layoutVersion={layoutVersion}
                    setLayouts={setLayouts}
                    snapshotLayouts={snapshotLayouts}
                    removeWidget={removeWidget}
                    updateWidgetSettings={updateWidgetSettings}
                    renderWidget={renderWidget}
                    settingsOpen={settingsOpen}
                    setSettingsOpen={setSettingsOpen}
                    isFetching={isFetching || salesByStoreFetching}
                />
            )}

            <MobileFooter
                dateType="date"
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                shiftDate={shiftDate}
                isCurrent={isToday}
                channel={channel}
                setChannel={(ch) => { setChannel(ch); savePreference(storeId, ch); }}
                includeReturns={includeReturns}
                setIncludeReturns={setIncludeReturns}
                includeMultibuys={includeMultibuys}
                setIncludeMultibuys={setIncludeMultibuys}
                compareFull={compareFull}
                setCompareFull={setCompareFull}
                onActionClick={() => setActionMenuOpen(x => !x)}
            />

            {/* Mobile Actions Menu */}
            {actionMenuOpen && (
                <div className="mobile-actions-menu-wrapper">
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                    <div className="mobile-actions-menu">
                        {!isEditing ? (
                            <>
                                {dataUpdatedAt > 0 && (
                                    <div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>
                                        Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                                    </div>
                                )}
                                <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">↻</span>
                                    <span className="nav-label">Refresh</span>
                                </button>
                                <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">✏️</span>
                                    <span className="nav-label">Edit Layout</span>
                                </button>
                                {isAdmin && (
                                    <button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text-tertiary)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                        <span className="nav-icon">🐛</span>
                                        <span className="nav-label">Debug SQL</span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--color-border)' }}>
                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                                        Primary Comparison:
                                    </span>
                                    <select
                                        value={primaryComparison}
                                        onChange={e => setPrimaryComparison(e.target.value as any)}
                                        style={{
                                            padding: '4px 8px', borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                                            color: 'var(--color-text)', fontSize: 'var(--text-xs)', flex: 1
                                        }}
                                    >
                                        <option value="none">None</option>
                                        <option value="yesterday">Day</option>
                                        <option value="lastWeek">Week</option>
                                        <option value="lastMonth">Month</option>
                                        <option value="lastYear">Year</option>
                                    </select>
                                </div>
                                <button className="nav-item" onClick={() => { setShowGallery(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">➕</span>
                                    <span className="nav-label">Add Widget</span>
                                </button>
                                <button className="nav-item" onClick={() => { setShowResetConfirm(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-error)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">↺</span>
                                    <span className="nav-label">Reset Layout</span>
                                </button>
                                <button className="nav-item" onClick={() => { handleCancel(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">✕</span>
                                    <span className="nav-label">Cancel Edits</span>
                                </button>
                                <button className="nav-item" onClick={() => { handleSave(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-primary)', width: '100%', textAlign: 'left', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <span className="nav-icon">💾</span>
                                    <span className="nav-label">Save Edits</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
